import { RootState } from "app/store";
import ReportCard from "components/card/ReportCard";
import Menu from "components/common/Menu";
import ReportsHeader from "components/header/ReportsHeader";
import { SideBarTabs } from "constants/tabs";
import AppCopy from "copy/AppCopy";
import useReport from "hook/useReport";
import { useTabActive } from "hook/useTabActive";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { CommonStyles } from "styles/CommonStyles";
import PageHeader from "components/common/PageHeader";
import EmptyState from "components/common/EmptyState";
import LoadMoreButton from "components/common/LoadMoreButton";

function ReportsPage() {
  const location = useLocation();
  const selectedReportId = String(location.state?.reportId || "");
  const { getReports } = useReport();
  const { reports } = useSelector((state: RootState) => state.report);
  const [loadingMoreReports, setLoadingMoreReports] = useState<boolean>(false);

  const loadData = async () => {
    await getReports({}, {});
  };

  useTabActive(SideBarTabs.Reports.name);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedReportId || !reports.length) return;
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(`report-${selectedReportId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [reports, selectedReportId]);

  const handleLoadMoreReports = async () => {
    await getReports(
      { setLoading: setLoadingMoreReports, offset: reports.length },
      { more: true }
    );
  };

  const { reportedTitle, reportStatusTitle } =
    AppCopy.reportsPage;

  return (
    <div className={`${CommonStyles.pageWrapper}`}>
      <Menu />
      <PageHeader
        title="Reports"
        description="Review user reports and moderation activity."
      />
      <div className={ClassName.container}>
        <section className={ClassName.reportSection}>
          <span className={ClassName.reportTitle}>{reportedTitle}</span>
          <div className={ClassName.reportListContainer}>
            <span className={ClassName.reportListTitle}>
              {reportStatusTitle}
            </span>
            <ReportsHeader />
            <div className={ClassName.reportList}>
              {reports.map((report, index) => {
                const reportId = String(report._id || index);
                return (
                  <div
                    id={`report-${reportId}`}
                    key={reportId}
                    className={
                      selectedReportId === reportId
                        ? ClassName.selectedTarget
                        : ClassName.target
                    }
                  >
                    <ReportCard report={report} />
                  </div>
                );
              })}
              {!reports.length ? (
                <EmptyState title="No reports" description="User reports will appear here." />
              ) : null}
              {reports && reports.length > 19 && (
                <LoadMoreButton
                  onClick={handleLoadMoreReports}
                  loading={loadingMoreReports}
                />
              )}
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}

export default ReportsPage;

const ClassName = {
  container: "flex flex-1 flex-col gap-5 text-primaryText",
  reportSection: "flex min-w-0 flex-col",
  reportListContainer:
    "flex w-full flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4",
  reportList: "flex max-h-[calc(100vh-250px)] min-h-56 flex-col overflow-y-auto",
  reportTitle: "mb-3 text-lg font-semibold",
  reportListTitle: "text-base font-semibold mt-4 mb-2",
  target: "rounded-xl transition-colors",
  selectedTarget: "rounded-xl bg-red-50 ring-2 ring-primary/40 transition-colors",
};
